import { AirportTransferDirection } from "@daytrip/legacy-enums";

/**
 * Calculates hours between two dates, rounded to 1 decimal place.
 *
 * Note: This function works correctly with dates in any timezone because
 * Date.getTime() always returns UTC milliseconds since epoch, making the
 * calculation timezone-agnostic.
 *
 * @param fromDate - Start date (earlier time)
 * @param toDate - End date (later time)
 * @returns Hours between dates, rounded to 1 decimal, or undefined if either date is null
 */
function calculateHoursBetweenDates(
    fromDate: Date | null | undefined,
    toDate: Date | null | undefined,
): number | undefined {
    if (!fromDate || !toDate) {
        return undefined;
    }

    const millisecondsDiff = toDate.getTime() - fromDate.getTime();
    const hours = millisecondsDiff / (1000 * 60 * 60);

    return Math.round(hours * 10) / 10;
}

/**
 * Calculates hours since order was created (purchase time).
 *
 * Note: The calculation correctly handles timezone differences because
 * Date.getTime() returns UTC milliseconds, making it timezone-agnostic.
 *
 * @param createdAt - Order creation date
 * @returns Hours since purchase, rounded to 1 decimal, or undefined if createdAt is null
 */
export function calculateHoursSincePurchase(createdAt: Date | null | undefined): number | undefined {
    return calculateHoursBetweenDates(createdAt, new Date());
}

/**
 * Calculates hours until order departure.
 *
 * Note: For accurate results, pass a UTC-normalized departure date
 * (e.g., from transformBookingToUTC). The calculation correctly handles
 * timezone differences because Date.getTime() returns UTC milliseconds.
 *
 * @param departureAt - Order departure date (preferably UTC-normalized)
 * @returns Hours until departure, rounded to 1 decimal, or undefined if departureAt is null
 *          Can be negative if departure is in the past
 */
export function calculateHoursUntilDeparture(departureAt: Date | null | undefined): number | undefined {
    return calculateHoursBetweenDates(new Date(), departureAt);
}

/**
 * Maps AirportTransferDirection enum to analytics string format.
 *
 * @param direction - Airport transfer direction enum value
 * @returns String representation: "to_airport", "from_airport", or "none"
 */
export function mapAirportDirection(
    direction: AirportTransferDirection | null | undefined,
): "to_airport" | "from_airport" | "none" {
    if (direction === AirportTransferDirection.ToAirport) {
        return "to_airport";
    }

    if (direction === AirportTransferDirection.FromAirport) {
        return "from_airport";
    }

    return "none";
}
